import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import { COLOR_SYSTEM } from "components/design/design-system";
import { AIReview, getPromptTypeKoreanName } from "common/models/types";
import { eventLog } from "utils/AmplitudeUtil";
import { useAIReviewPin } from "hooks/useAIReviewPin";
import SectionHeader from "./SectionHeader";

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ReportCard = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding: 24px;
`;

const ReportList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReportRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ReportNumber = styled.div`
  flex-shrink: 0;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-variant-numeric: tabular-nums;
`;

const ReportItem = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  padding: 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Skyblue")?.[200]};
    background: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  }
`;

const ReportItemContent = styled.div`
  display: flex;
  align-items: center;
  padding-right: 110px;
`;

const DateBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  white-space: nowrap;
`;

const ReportIconWrapper = styled.div`
  background: ${COLOR_SYSTEM.get("Skyblue")?.[100]};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
`;

const PinButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ReportDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReportDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReportDetailRow = styled.div`
  display: flex;
  align-items: center;
`;

const ViewAllLink = styled.button`
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 2px;
  border-radius: 4px;
  transition: background 0.2s ease;

  &:hover {
    background: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  }
`;

interface DashboardReportProps {
  reviewList: AIReview[];
  reviews: {
    [id: number]: AIReview;
  };
  onSelectReport: (patientId: number, reportId: number) => void;
}

const getPatientName = (report: AIReview): string => {
  return report.patientId ? `환자${report.patientId}` : "환자 정보 없음";
};

const formatDate = (date?: Date | string): string => {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/**
 * "최근 보고서" 섹션 전용 컴포넌트.
 * 핀 보고서 포함 여부는 상위에서 결정하며, 핀된 보고서는 핀 아이콘으로 구분 표시한다.
 */
export const DashboardReport: React.FC<DashboardReportProps> = ({
  reviewList,
  reviews,
  onSelectReport,
}) => {
  const navigate = useNavigate();
  const { togglePin, isLoading: isPinLoading } = useAIReviewPin();

  const handleItemClick = (report: AIReview) => {
    eventLog("대시보드보고서선택", {
      reportId: report.id,
      patientId: report.patientId,
    });
    if (report.patientId && report.id) {
      onSelectReport(report.patientId, report.id);
    }
  };

  const handlePinClick = (e: React.MouseEvent, reviewId: number) => {
    e.stopPropagation();
    togglePin(reviewId);
  };

  const handleViewAll = () => {
    eventLog("대시보드_목록_보기_이동", { tab: "recent" });
    navigate("/mypage/ai-reviews?tab=recent");
  };

  const recentReports = (reviewList || []).slice(0, 10);

  const renderReportItem = (report: AIReview, index: number) => {
    const isPinned = !!report.pinnedAt;
    return (
    <ReportRow key={report.id}>
      <ReportNumber>{index + 1}</ReportNumber>
      <ReportItem onClick={() => handleItemClick(report)}>
      <DateBadge>
        <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
          {formatDate(report.reviewDate || report.createdAt)}
        </Text>
      </DateBadge>
      <ReportItemContent>
        <ReportIconWrapper>
          <Icon name="Document" size={20} color={COLOR_SYSTEM.get("Skyblue")?.[700]} />
        </ReportIconWrapper>
        <ReportDetails>
          <ReportDetailHeader>
            <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[800]}>
              보고서 #{report.id} - {getPatientName(report)}
            </Text>
          </ReportDetailHeader>
          <ReportDetailRow>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
              보고서 종류: {getPromptTypeKoreanName(report.promptType)}
            </Text>
          </ReportDetailRow>
          {report.memo && (
            <ReportDetailRow>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Skyblue")?.[500]}>
                메모: {report.memo}
              </Text>
            </ReportDetailRow>
          )}
        </ReportDetails>
      </ReportItemContent>
      <PinButton
        onClick={(e) => handlePinClick(e, report.id)}
        disabled={isPinLoading}
        aria-label={isPinned ? "고정 해제" : "고정"}
      >
        <Icon
          name={isPinned ? "Pin 02 Filled" : "Pin 02"}
          size={16}
          color={isPinned ? COLOR_SYSTEM.get("Yellow")?.[500] : COLOR_SYSTEM.get("Gray")?.[400]}
        />
      </PinButton>
      </ReportItem>
    </ReportRow>
    );
  };

  return (
    <ReportContainer>
      <ReportCard>
        <SectionHeader
          title="최근 보고서"
          description={
            recentReports.length > 0 ? `최근 생성한 ${recentReports.length}건입니다` : undefined
          }
          rightSlot={
            recentReports.length > 0 ? (
              <ViewAllLink onClick={handleViewAll}>
                전체 보기
                <Icon name="Chevron Right" size={14} color={COLOR_SYSTEM.get("Skyblue")?.[500]} />
              </ViewAllLink>
            ) : undefined
          }
        />
        {recentReports.length > 0 ? (
          <ReportList>
            {recentReports.map((reportID, index) => {
              const report = reviews[reportID.id];
              if (!report) return null;
              return renderReportItem(report, index);
            })}
          </ReportList>
        ) : (
          <div style={{ padding: "24px", textAlign: "center" }}>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              아직 생성된 보고서가 없습니다.
            </Text>
          </div>
        )}
      </ReportCard>
    </ReportContainer>
  );
};

export default DashboardReport;
